<template>
  <div
    class="c-popover"
    :class="classy"
    :style="style"
  >
    <header
      v-if="$slots.header"
      class="header"
    >
      <slot name="header" />
    </header>
    <div class="content">
      <slot />
    </div>
  </div>
</template>

<script>
import getParents from '@/helpers/getParents'
import * as util from 'vue-convenia-util'

const is = util.validate.is

export default {
  props: {
    target: {
      type: [String, HTMLElement, Object],
      required: true
    },
    arrow: {
      type: Boolean,
      default: true
    },
    position: {
      validator (value) {
        const positions = ['top']
        const directions = ['right', 'center', 'left']
        const valid = Array.isArray(value) && positions.includes(value[0]) && directions.includes(value[1])
        return valid
      }
    },
    noPadding: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      style: {}
    }
  },
  computed: {
    classy () {
      const [ position, direction ] = this.position
      const classy = {
        [`-arrow-${position}-${direction}`]: this.arrow,
        '-no-arrow': !this.arrow,
        '-header': this.$slots.header,
        '-no-padding': this.noPadding
      }
      return classy
    },
    element () {
      const element = is(this.target, 'String') ? document.querySelector(this.target)
        : this.target instanceof HTMLElement ? this.target
          : this.target.$el
      return element
    }
  },
  watch: {
    target: {
      handler: 'calcStyle',
      immediate: true
    }
  },
  mounted () {
    window.addEventListener('scroll', this.calcStyle)
    window.addEventListener('resize', this.calcStyle)
    window.addEventListener('click', this.blurEmitter)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.calcStyle)
    window.removeEventListener('resize', this.calcStyle)
    window.removeEventListener('click', this.blurEmitter)
  },
  methods: {
    calcStyle () {
      if (!this.element || !this.$el) { return setTimeout(this.calcStyle, 250) }

      const popoverBB = this.$el.getBoundingClientRect()
      const targetBB = this.element.getBoundingClientRect()
      let posX = targetBB.left + targetBB.width / 2

      posX = this.position[1] === 'right' ? posX - popoverBB.width + 4
        : this.position[1] === 'center' ? posX - popoverBB.width / 2
          : posX - 4

      const style = { top: (targetBB.top + targetBB.height + 8) + 'px', left: posX + 'px' }
      this.style = style
    },
    blurEmitter (event) {
      const targets = event.target ? [event.target, ...getParents(event.target)] : []
      const isTarget = targets.includes(this.element)
      const isOutside = !targets.includes(this.$el)
      if (!isTarget && isOutside) {
        this.$emit('blur')
      }
    }
  }
}
</script>

<style lang="scss">
$c-popover-arrow-size: .25rem          !default;
$c-popover-background: #fff          !default;
$c-popover-border: 1px solid #eaedef !default;
$c-popover-header-background: #fdfdfd !default;

@mixin box-shadow-as-border($border, $position: top) {
  $size: calc(nth($border, 1) / 2);
  $color: nth($border, 3);

  @if ($position == top) {
    box-shadow: -#{$size} -#{$size} 0 #{$size} $color;
  }
}

%c-popover-arrow-top {
  &::before {
    @extend %c-popover-arrow;
    @include box-shadow-as-border($c-popover-border, top);
    top: 0;
    transform: rotate(45deg);
    border-color: $c-popover-background;
  }

  &.-header::before { border-color: $c-popover-header-background; }
}

.c-popover {
  & {
    position: fixed;
    background-color: $c-popover-background;
    border: $c-popover-border;
    border-radius: 3px;
    box-shadow:
      0 0 1px rgba(#8e92a7, 0.1),
      0 0 6px rgba(#8e92a7, 0.2);
  }

  & > .header,
  & > .content {
    box-sizing: border-box;
    padding: 18px;
  }

  & > .header { @extend %c-popover-header; }

  &.-no-padding {
    & > .header,
    & > .content {
      padding: 18px 0;
    }
  }

  &.-arrow-top-center {
    @extend %c-popover-arrow-top;
    &::before {
      left: 50%;
      transform: translate(-50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }

  &.-arrow-top-right {
    @extend %c-popover-arrow-top;
    &::before {
      right: .75rem;
      transform: translate(-50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }

  &.-arrow-top-left {
    @extend %c-popover-arrow-top;
    &::before {
      left: 0;
      transform: translate(50%, -$c-popover-arrow-size) rotate(45deg);
    }
  }
}

%c-popover-arrow {
  position: absolute;
  width: 0;
  height: 0;
  z-index: 1;
  content: '';
  border-width: $c-popover-arrow-size $c-popover-arrow-size $c-popover-arrow-size $c-popover-arrow-size;
  border-style: solid solid solid solid;
  background-color: $c-popover-background;
  // TODO BOXSHADOW
}

%c-popover-header {
  background-color: $c-popover-header-background;
  border-bottom: 1px solid #e9eaee;
}
</style>



// WEBPACK FOOTER //
// src/components/convenia-components/CPopover.vue